import React from 'react';

import { DateTime } from 'luxon';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';

import * as api from '../../api.js';

import { useAuth } from '../../AuthContext';
import { useRealtimeTrigger } from '../../hooks/useRealtimeTrigger';

const ALERT_ERRORS = {
    'notify/duplicate-shift-ids': 'The same shift was selected more than once.',
    'notify/shift-not-found': 'A selected shift could not be found for this patrol.',
    'notify/shift-cancelled': 'A selected shift has been cancelled.',
    'notify/shift-not-today': 'A selected shift is not currently active, so an urgent request cannot be opened for it.'
};

// How long before its start a shift becomes eligible; must match the API (patrol360-api notify alerts).
export const SHIFT_ALERT_LEAD_MS = 6 * 60 * 60 * 1000;
const SHIFT_LOOKBACK_MS = 24 * 60 * 60 * 1000;
const DEFAULT_SHIFT_LENGTH_MS = 12 * 60 * 60 * 1000;

// Mirrors the API rule: not cancelled and start - lead <= now <= end (end defaults to start + 12h).
export const isShiftEligible = (shift) => {
    const start = Number(shift?.start);

    if (!start || shift.cancelled) {
        return false;
    }

    const end = Number(shift.end) || start + DEFAULT_SHIFT_LENGTH_MS;
    const now = Date.now();

    return now >= start - SHIFT_ALERT_LEAD_MS && now <= end;
};

export const Alerts = ({ currentShift }) => {
    const [ sending, setSending ] = React.useState(false);
    const [ error, setError ] = React.useState(null);
    const [ recipients, setRecipients ] = React.useState([]);
    const [ alerts, setAlerts ] = React.useState([]);
    const [ loadingAlerts, setLoadingAlerts ] = React.useState(false);

    const [ allShifts, setAllShifts ] = React.useState([]);
    const [ shiftsError, setShiftsError ] = React.useState(false);
    const [ loadingShifts, setLoadingShifts ] = React.useState(false);
    const [ selectedShiftIds, setSelectedShiftIds ] = React.useState([]);
    const [ shiftSelectionError, setShiftSelectionError ] = React.useState(false);

    const { token } = useAuth();

    const [ selectedType, setSelectedType ] = React.useState('chairLiftEvac');

    const [ confirmDialog, setConfirmDialog ] = React.useState({
        open: false,
        type: null,
        close: false,
        id: null
    });

    // Verification code to prevent casual testing in-production while rolling out
    const [ verificationCode, setVerificationCode ] = React.useState('');
    const [ verificationError, setVerificationError ] = React.useState(false);

    const notificationTypes = [
        {
            key: 'chairLiftEvac',
            title: 'Chair Lift Evacuation',
            description: 'Notify patrollers about chair lift evacuation',
            color: 'error'
        },
        {
            key: 'urgentRequest',
            title: 'Urgent Shift Request',
            description: 'Notify patrollers for urgent shift request',
            color: 'warning'
        }
    ];

    const handleAlertUpdate = React.useCallback(async (alertId) => {
        try {
            const alert = await api.getAlertEvent(alertId, token);

            if (alert && !alert.error) {
                setAlerts(prev => {
                    const index = prev.findIndex(s => s.id === alertId);

                    if (index !== -1) {
                        const updatedAlerts = [ ...prev ];

                        updatedAlerts[ index ] = alert;

                        return updatedAlerts;
                    } else {
                        return [ ...prev, alert ];
                    }
                });

                setRecipients(alert.recipients || []);
            } else {
                console.error('Invalid alert data or error:', alert);

                setError(alert?.error || 'Failed to fetch alert data');
            }
        } catch (error) {
            console.error('Failed to update alert:', error);

            setError(error.message || 'Failed to update alert');
        }
    }, [ token ]);

    useRealtimeTrigger('manager', 'alerts', handleAlertUpdate);

    React.useEffect(() => {
        const fetchAlerts = async () => {
            setLoadingAlerts(true);
            setError(null);

            try {
                const alerts = await api.getAllAlertEvents(token);

                if (alerts && !alerts.error) {
                    setAlerts(alerts);
                } else {
                    setError(alerts?.error || 'Failed to fetch alerts');
                }
            } catch (error) {
                setError(error.message || 'Failed to fetch alerts');
            } finally {
                setLoadingAlerts(false);
            }
        };

        fetchAlerts();
    }, [ token ]);

    // Fetch the shift list at most once per mount, and only when it is needed: the
    // urgent-request picker is open, or an existing alert needs its shift labelled.
    // No retry on failure (a failed fetch leaves the picker empty with a message).
    const shiftsRequested = React.useRef(false);
    const needShifts = (confirmDialog.open && selectedType === 'urgentRequest') || alerts.some(a => a.shiftId);

    React.useEffect(() => {
        if (!needShifts || shiftsRequested.current || !token) {
            return;
        }

        shiftsRequested.current = true;

        const fetchShifts = async () => {
            setLoadingShifts(true);

            try {
                // Wide lower bound so shifts that started before midnight but are still running are included;
                // isShiftEligible is the real filter.
                const shifts = await api.getShifts(token, Date.now() - SHIFT_LOOKBACK_MS);

                if (Array.isArray(shifts)) {
                    setAllShifts(shifts);
                } else {
                    setShiftsError(true);
                }
            } catch (e) {
                console.error('Failed to fetch shifts:', e);
                setShiftsError(true);
            } finally {
                setLoadingShifts(false);
            }
        };

        fetchShifts();
    }, [ needShifts, token ]);

    // Only shifts currently eligible for an alert, matching the API rule
    const todayShifts = React.useMemo(() => allShifts.filter(isShiftEligible), [ allShifts ]);

    // Default the picker to the HC's current shift when opening an urgentRequest dialog
    React.useEffect(() => {
        if (confirmDialog.open && selectedType === 'urgentRequest') {
            const defaultId = currentShift?.id && todayShifts.some(s => s.id === currentShift.id)
                ? [ currentShift.id ]
                : [];

            setSelectedShiftIds(defaultId);
            setShiftSelectionError(false);
        }
    }, [ confirmDialog.open, selectedType, currentShift?.id, todayShifts ]);

    const handleTypeChange = (event) => {
        setSelectedType(event.target.value);
        setShiftSelectionError(false);
    };

    const handleShiftToggle = (shiftId) => {
        setSelectedShiftIds(prev => prev.includes(shiftId) ? prev.filter(id => id !== shiftId) : [ ...prev, shiftId ]);
        setShiftSelectionError(false);
    };

    const handleConfirmSend = async () => {
        setSending(true);
        setError(null);

        try {
            const resp = confirmDialog.open
                ? await api.openAlertEvent(selectedType, selectedType === 'urgentRequest' ? selectedShiftIds : null, token)
                : await api.closeAlertEvent(confirmDialog.id, token);

            if (resp.error) {
                setError(ALERT_ERRORS[ resp.error ] || resp.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSending(false);
            setConfirmDialog({ open: false, type: null, close: false, id: null });
        }
    };

    const handleCloseDialog = () => {
        setConfirmDialog({ open: false, type: null, close: false, id: null });
        setVerificationCode('');
        setVerificationError(false);
        setShiftSelectionError(false);
    };

    const selectedTypeData = notificationTypes.find(nt => nt.key === selectedType);
    const dialogType = confirmDialog.close ? confirmDialog.type : selectedType;
    const dialogTypeData = notificationTypes.find(nt => nt.key === dialogType);
    const requiresVerification = dialogType === 'chairLiftEvac';
    const expectedVerificationCode = DateTime.now().toFormat('yyyyLLddHH');
    const verificationSatisfied = !requiresVerification || verificationCode === expectedVerificationCode;

    return (
        <Card>
            <CardHeader
                avatar={ <NotificationsIcon /> }
                title={
                    <Typography variant="h6">
                        Active Alerts
                    </Typography>
                }
                action={
                    <Button
                        variant="outlined"
                        color="error"
                        size='small'
                        onClick={ () => setConfirmDialog({ open: true, type: selectedType, close: false }) }
                        disabled={ sending }
                        startIcon={ <Add /> }
                    >
                        Create Alert
                    </Button>
                }
            />

            <CardContent>
                { error && (
                    <Alert variant='filled' severity="error" sx={ { mb: 2 } }>
                        { error }
                    </Alert>
                ) }

                <Divider sx={ { mb: 2 } } />

                <Stack spacing={ 2 } direction="column" alignItems="center" sx={ { mb: 2 } }>
                    { loadingAlerts ? (
                        <Typography>Loading alerts...</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Shift</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell align='center'>Notified / Ack&apos;ed</TableCell>
                                    <TableCell align='center'>En Route</TableCell>
                                    <TableCell align='center'>Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                { alerts.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={ 6 }>No active alerts</TableCell>
                                    </TableRow>
                                ) }

                                { alerts.map(alert => {
                                    const recipientsNotified = Array.isArray(alert.recipients) ? alert.recipients.filter(r => r.status === 'notified').length : 0;
                                    const recipientsAcknowledged = Array.isArray(alert.recipients) ? alert.recipients.filter(r => r.ack).length : 0;
                                    const recipientsConfirmed = Array.isArray(alert.recipients) ? alert.recipients.filter(r => r.ack && r.eta).length : 0;
                                    const alertShift = alert.shiftId ? allShifts.find(s => s.id === alert.shiftId) : null;
                                    const shiftLabel = alertShift
                                        ? `${ alertShift.name || alertShift.event?.nameShort || 'Shift' } @ ${ DateTime.fromMillis(alertShift.start).toLocaleString(DateTime.TIME_SIMPLE) }`
                                        : alert.shiftId ? alert.shiftId : '-';

                                    return (
                                        <TableRow key={ alert.id }>
                                            <TableCell>{ alert.type || '-' }</TableCell>

                                            <TableCell>{ shiftLabel }</TableCell>

                                            <TableCell>{ alert.createdAt ? DateTime.fromMillis(alert.createdAt).toLocaleString(DateTime.DATETIME_SHORT) : '-' }</TableCell>

                                            <TableCell align='center'>{ `${ recipientsNotified } / ${ recipientsAcknowledged }` }</TableCell>

                                            <TableCell align='center'>{ recipientsConfirmed }</TableCell>

                                            <TableCell align='center'>
                                                <Button
                                                    size="small"
                                                    color="secondary"
                                                    variant="outlined"
                                                    onClick={ () => setConfirmDialog({ open: false, type: alert.type, close: true, id: alert.id }) }
                                                    startIcon={ <Close /> }
                                                    disabled={ sending }
                                                >
                                                    Close
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }) }
                            </TableBody>
                        </Table>
                    ) }
                            
                    <Divider sx={ { my: 2 } } />

                    { recipients && recipients.length > 0 && (
                        <>
                            <Typography variant="subtitle1" sx={ { mb: 1 } }>
                                Recipients
                            </Typography>

                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name / User</TableCell>
                                        <TableCell>Contact</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>ETA</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    { recipients.map(r => r.id).map((key) => {
                                        const r = recipients?.[ key ] || recipients.find(x => x.id === key) || {};

                                        return (
                                            <TableRow key={ key }>
                                                <TableCell>{ r.name || r.id || key }</TableCell>
                                                <TableCell>{ r.phoneNumber }</TableCell>
                                                <TableCell>{ r.ack && r.eta ? 'Confirmed' : r.ack && !r.eta ? 'Unable' : 'Pending' }</TableCell>
                                                <TableCell>{ r.eta ? DateTime.fromMillis(r.updatedAt).plus({ minutes: r.eta ?? 0 }).toLocaleString(DateTime.TIME_SIMPLE) : '-' }</TableCell>
                                            </TableRow>
                                        );
                                    }) }
                                </TableBody>
                            </Table>
                        </>
                    ) }
                </Stack>

                <Dialog
                    open={ confirmDialog.open || confirmDialog.close }
                    onClose={ handleCloseDialog }
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Confirm Open Alert</DialogTitle>

                    <DialogContent>
                        <FormControl component="fieldset" sx={ { mb: 3 } } disabled={ confirmDialog.close }>
                            <FormLabel component="legend">Notification Type</FormLabel>

                            <RadioGroup
                                value={ selectedType }
                                onChange={ handleTypeChange }
                                row
                            >
                                { notificationTypes.map((type) => (
                                    <FormControlLabel
                                        key={ type.key }
                                        value={ type.key }
                                        control={ <Radio /> }
                                        label={ type.title }
                                        sx={ { color: type.color === 'inherit' ? 'text.primary' : `${ type.color }.main` } }
                                    />
                                )) }
                            </RadioGroup>

                            { selectedTypeData && (
                                <Typography variant="body2" color="text.secondary" sx={ { mt: 1 } }>
                                    { selectedTypeData.description }
                                </Typography>
                            ) }
                        </FormControl>

                        { confirmDialog.open && selectedType === 'urgentRequest' && (
                            <FormControl component="fieldset" error={ shiftSelectionError } sx={ { mb: 3, display: 'block' } }>
                                <FormLabel component="legend">Shifts needing help</FormLabel>

                                { loadingShifts ? (
                                    <Stack direction="row" alignItems="center" spacing={ 1 } sx={ { mt: 1 } }>
                                        <CircularProgress size={ 16 } />

                                        <Typography variant="body2" color="text.secondary">
                                            Loading shifts...
                                        </Typography>
                                    </Stack>
                                ) : todayShifts.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={ { mt: 1 } }>
                                        { shiftsError ? 'Unable to load shifts.' : 'No shifts today.' }
                                    </Typography>
                                ) : (
                                    <FormGroup>
                                        { todayShifts.map(shift => (
                                            <FormControlLabel
                                                key={ shift.id }
                                                control={
                                                    <Checkbox
                                                        checked={ selectedShiftIds.includes(shift.id) }
                                                        onChange={ () => handleShiftToggle(shift.id) }
                                                    />
                                                }
                                                label={ `${ shift.name || shift.event?.nameShort || 'Shift' } — ${ DateTime.fromMillis(shift.start).toLocaleString(DateTime.TIME_SIMPLE) }${ shift.end ? ` – ${ DateTime.fromMillis(shift.end).toLocaleString(DateTime.TIME_SIMPLE) }` : '' }` }
                                            />
                                        )) }
                                    </FormGroup>
                                ) }

                                { shiftSelectionError && (
                                    <FormHelperText>Select at least one shift.</FormHelperText>
                                ) }
                            </FormControl>
                        ) }

                        <Divider sx={ { mb: 2 } } />

                        <Typography sx={ { color: `${ dialogTypeData?.color || 'warning' }.main` } }>
                            Are you sure you want to { confirmDialog.close ? 'CLOSE' : 'OPEN' } the { dialogType?.replace(/([A-Z])/g, ' $1').toUpperCase() } alert?
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={ { mt: 1 } }>
                            This action will notify all patrollers that have opted in to receive this notification.
                        </Typography>

                        <Divider sx={ { my: 2 } } />

                        { requiresVerification && (
                            <>
                                <Typography>
                                    To confirm, please enter verification code:
                                </Typography>

                                <TextField
                                    label="Verification Code"
                                    value={ verificationCode }
                                    onChange={ (e) => {
                                        const v = (e.target.value || '').trim();
                                        setVerificationCode(v);
                                        setVerificationError(false);
                                    } }
                                    helperText={ verificationError && 'Verification code is incorrect.' }
                                    error={ verificationError }
                                    margin='normal'
                                    fullWidth
                                />

                                <Divider sx={ { mt: 2 } } />
                            </>
                        ) }
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={ handleCloseDialog } disabled={ sending }>
                            Cancel
                        </Button>

                        <Button
                            onClick={ async () => {
                                if (requiresVerification && verificationCode !== expectedVerificationCode) {
                                    setVerificationError(true);

                                    return;
                                }

                                if (confirmDialog.open && selectedType === 'urgentRequest' && selectedShiftIds.length === 0) {
                                    setShiftSelectionError(true);

                                    return;
                                }

                                await handleConfirmSend();
                            } }
                            variant="contained"
                            color="primary"
                            disabled={ sending || !verificationSatisfied || (confirmDialog.open && selectedType === 'urgentRequest' && selectedShiftIds.length === 0) }
                        >
                            { sending ? 'Sending...' : confirmDialog.close ? 'Close' : 'Open' }
                        </Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
};